import React, { useContext, useEffect, useState } from "react";
import { yesOrNoOptions } from "~/functions/selectPopoverOptions";
import Input from "../RegisterForm/Input";
import InputLine from "../RegisterForm/InputLine";
import SelectPopover from "../RegisterForm/SelectPopover";
import { Zoom, toast } from "react-toastify";
import { Trash2 } from "lucide-react";
import { Scope } from "@unform/core";
import { getPriceLists } from "~/functions";
import DatePicker from "../RegisterForm/DatePicker";
import { format, parseISO } from "date-fns";

function PricesSimulation({
  student = null,
  InputContext = null,
  generalForm = null,
  showAdmissionDiscounts = false,
  isAdmissionDiscountChangable = false,
  showFinancialDiscounts = false,
  isFinancialDiscountChangable = false,
  FullGridContext = null,
  recurrence = false,
  settlement = false,
  tuitionOnly = false,
  totalAmount = 0,
  setHasDiscount = () => null,
  onTotalsChange = null,
}) {
  const { setSuccessfullyUpdated } = useContext(FullGridContext);
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [priceLists, setPriceLists] = useState(null);
  const [discountLists, setDiscountLists] = useState(null);
  const [discountOptions, setDiscountOptions] = useState([]);
  const [canModifyDiscounts, setCanModifyDiscounts] = useState(true);
  const [pricingLoading, setPricingLoading] = useState(true);
  const [pricingError, setPricingError] = useState(false);
  const [pricingRetry, setPricingRetry] = useState(0);
  const filialId = student?.searchFields?.filial_id;
  const substatusId = student?.searchFields?.processsubstatus_id;
  const suppliedPrices = student?.priceLists;
  const suppliedDiscounts = student?.discountLists;

  useEffect(() => {
    let cancelled = false;
    setPricingLoading(true);
    setPricingError(false);
    async function loadPricing() {
      try {
        const canReuse = suppliedPrices && Array.isArray(suppliedDiscounts) &&
          Number(suppliedPrices.filial_id) === Number(filialId) &&
          Number(suppliedPrices.processsubstatus_id) === Number(substatusId);
        const data = canReuse
          ? { priceLists: suppliedPrices, discountLists: suppliedDiscounts }
          : await getPriceLists({ filial_id: filialId, processsubstatus_id: substatusId });
        if (cancelled) return;
        let discounts = data.discountLists || [];
        if (recurrence) discounts = discounts.filter((discount) =>
          discount.type === "Financial" && !discount.applied_at?.includes("Settlement"));
        if (settlement) discounts = discounts.filter((discount) =>
          discount.type === "Financial" && discount.applied_at?.includes("Settlement"));
        setPriceLists(data.priceLists);
        setDiscountLists(discounts);
        setDiscountOptions(discounts.map((discount) => ({
          value: discount.id,
          label: discount.name + " - " + discount.value + (discount.percent ? "%" : "$") + " - " + discount.applied_at,
          applied_at: discount.applied_at,
          type: discount.type,
        })));
      } catch (err) {
        if (!cancelled) setPricingError(true);
      } finally {
        if (!cancelled) setPricingLoading(false);
      }
    }
    loadPricing();
    return () => { cancelled = true; };
  }, [filialId, substatusId, suppliedPrices, suppliedDiscounts, recurrence, settlement, pricingRetry]);

  useEffect(() => {
    const ordered = [...(student?.discounts || [])].sort((a, b) => {
      const order = (row) => row?.order_index !== null && Number.isFinite(Number(row?.order_index))
        ? Number(row.order_index) : Number.MAX_SAFE_INTEGER;
      return order(a) - order(b);
    });
    setAppliedDiscounts(ordered.filter((row) => row.discount && (
      (showFinancialDiscounts && row.discount.type === "Financial") ||
      (showAdmissionDiscounts && row.discount.type === "Admission")
    )).map((row) => ({ ...row.discount, start_date: row.start_date,
      end_date: row.end_date, has_paid_invoice: row.has_paid_invoice ?? false })));
  }, [student?.discounts, showFinancialDiscounts, showAdmissionDiscounts]);
  useEffect(() => {
    function calculateDiscounts() {
      let tuitionBase = totalAmount ? totalAmount : priceLists.tuition;
      let registrationBase = tuitionOnly ? 0 : priceLists.registration_fee;
      let discountAmount = 0;

      if (!appliedDiscounts.length) {
        setHasDiscount(false);
        setTotalDiscount(0);
        if (onTotalsChange) {
          onTotalsChange({
            totalDiscount: 0,
            totalTuition:
              settlement || recurrence || tuitionOnly
                ? tuitionBase
                : tuitionBase + registrationBase + priceLists.book,
          });
        }
        return;
      }
      setHasDiscount(true);

      appliedDiscounts.forEach((curr) => {
        const appliedAt = curr.applied_at || "";
        const isTuition =
          appliedAt.includes("Tuition") || appliedAt.includes("Settlement");
        const isRegistration = appliedAt.includes("Registration");
        if (!isTuition && !isRegistration) {
          return;
        }

        if (isRegistration) {
          const value = Number(curr.value) || 0;
          const appliedValue = curr.percent
            ? (registrationBase * value) / 100
            : value;
          const safeApplied = Math.min(appliedValue, registrationBase);
          registrationBase = Math.max(0, registrationBase - safeApplied);
          discountAmount += safeApplied;
        }
        if (isTuition) {
          const value = Number(curr.value) || 0;
          const appliedValue = curr.percent ? (tuitionBase * value) / 100 : value;
          const safeApplied = Math.min(appliedValue, tuitionBase);
          tuitionBase = Math.max(0, tuitionBase - safeApplied);
          discountAmount += safeApplied;
        }
      });

      setTotalDiscount(discountAmount);
      if (onTotalsChange) {
        onTotalsChange({
          totalDiscount: discountAmount,
          totalTuition:
            settlement || recurrence || tuitionOnly
              ? tuitionBase
              : tuitionBase + registrationBase + priceLists.book,
        });
      }
    }
    if (priceLists && discountLists) {
      calculateDiscounts();
    }
  }, [appliedDiscounts, priceLists, discountLists, tuitionOnly, totalAmount, settlement, recurrence]);

  function handleDiscount({
    id,
    start_date = null,
    end_date = null,
    el,
    discountLists = null,
  }) {
    console.log("[PricesSimulation] handleDiscount called:", {
      action: el?.value ? "APPLY" : "REMOVE",
      id,
      start_date,
      end_date,
      appliedDiscounts_before: appliedDiscounts,
    });
    if (!id) {
      toast("not defined");
      return;
    }
    let newDiscounts = discountLists;

    const discount = discountLists.find((discount) => discount.id === id);
    if (el.value) {
      if (appliedDiscounts.find((discount) => discount.id === id)) {
        toast("Discount already applied!", {
          autoClose: 1000,
          type: "error",
          transition: Zoom,
        });
        return;
      }
      newDiscounts = [
        ...appliedDiscounts,
        {
          id: discount.id,
          start_date: start_date ? format(start_date, "yyyy-MM-dd") : null,
          end_date: end_date ? format(end_date, "yyyy-MM-dd") : null,
          name: discount.name,
          value: discount.value,
          percent: discount.percent,
          applied_at: discount.applied_at,
          type: discount.type,
        },
      ];
      toast("Discount applied!", {
        autoClose: 1000,
        type: "success",
        transition: Zoom,
      });
    } else if (!el.value) {
      newDiscounts = appliedDiscounts.filter((discount) => discount.id !== id);
      toast("Discount removed!", {
        autoClose: 1000,
        type: "success",
        transition: Zoom,
      });
    }
    console.log(
      "[PricesSimulation] appliedDiscounts_after:",
      newDiscounts
    );
    setSuccessfullyUpdated(false);
    setAppliedDiscounts(newDiscounts);
  }

  if (pricingLoading) {
    return <InputLine title="Prices and Discounts"><p className="text-xs text-gray-500" role="status">Loading prices and discounts...</p></InputLine>;
  }
  if (pricingError) {
    return <InputLine title="Prices and Discounts"><p className="text-xs text-red-600" role="alert">Could not load prices and discounts.</p><button type="button" onClick={() => setPricingRetry((value) => value + 1)} className="rounded border px-3 py-2 text-xs">Retry</button></InputLine>;
  }
  if (!priceLists || !discountLists) {
    return <InputLine title="Prices and Discounts"><p className="text-xs text-gray-500">No price list is configured for this branch and process substatus.</p></InputLine>;
  }

  return (
    <Scope path="prices">
      <InputLine title="Prices Simulation">
        <table className="table-auto w-full text-center">
          <thead className="bg-slate-200 rounded-lg overflow-hidden">
            <tr>
              {!recurrence && !settlement && !tuitionOnly && (
                <>
                  <th className="w-1/6">Registration Fee</th>
                  {/* <th className="w-1/6">Books</th> */}
                  <th className="w-1/6">Tuition in Advanced</th>
                </>
              )}
              <th className="w-1/6">Tuition</th>
              <th className="w-1/6">Discount</th>
              <th className="w-1/6">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              {!recurrence && !settlement && !tuitionOnly && (
                <>
                  <td>
                    <Input
                      type="text"
                      name="registration_fee"
                      defaultValue={priceLists.registration_fee.toFixed(2)}
                      centeredText={true}
                      placeholder="$ 0.00"
                      className="text-center"
                      readOnly={true}
                      InputContext={InputContext}
                    />
                  </td>
                  {/* <td>
                    <Input
                      type="text"
                      name="books"
                      value={priceLists.book.toFixed(2)}
                      centeredText={true}
                      placeholder="$ 0.00"
                      className="text-center"
                      readOnly={true}
                      InputContext={InputContext}
                    />
                  </td> */}
                  <td>
                    <SelectPopover
                      name="tuition_in_advance"
                      defaultValue={yesOrNoOptions.find(
                        (option) =>
                          option.value === priceLists.tuition_in_advance
                      )}
                      centeredText={true}
                      readOnly={true}
                      options={yesOrNoOptions}
                      InputContext={InputContext}
                    />
                  </td>
                </>
              )}
              <td>
                <Input
                  type="text"
                  name="tuition_original_price"
                  defaultValue={
                    totalAmount
                      ? totalAmount.toFixed(2)
                      : priceLists.tuition.toFixed(2)
                  }
                  centeredText={true}
                  placeholder="$ 0.00"
                  className="text-center"
                  readOnly={true}
                  InputContext={InputContext}
                />
              </td>
              <td>
                <Input
                  type="text"
                  name="total_discount"
                  defaultValue={totalDiscount.toFixed(2)}
                  centeredText={true}
                  placeholder="$ 0.00"
                  className="text-center"
                  readOnly={true}
                  InputContext={InputContext}
                />
              </td>
              <td>
                <Input
                  type="text"
                  name="total_tuition"
                  defaultValue={(!recurrence && !settlement && !tuitionOnly
                    ? priceLists.registration_fee +
                      priceLists.book +
                      (totalAmount ? totalAmount : priceLists.tuition) -
                      totalDiscount
                    : (totalAmount ? totalAmount : priceLists.tuition) -
                      totalDiscount
                  ).toFixed(2)}
                  centeredText={true}
                  placeholder="$ 0.00"
                  className="text-center"
                  readOnly={true}
                  InputContext={InputContext}
                />
              </td>
            </tr>
          </tbody>
        </table>
      </InputLine>
      {(showAdmissionDiscounts || showFinancialDiscounts) && (
        <>
          <InputLine>
            <table className="table-auto w-full text-center">
              <thead className="bg-slate-200 rounded-lg overflow-hidden">
                <tr>
                  <th>Discount name</th>
                  <th className="w-1/7">Value</th>
                  {showFinancialDiscounts && (
                    <>
                      <th className="w-1/7">Start Date</th>
                      <th className="w-1/7">End Date</th>
                    </>
                  )}
                  <th className="w-1/7">Type</th>
                  <th className="w-1/7">Applied At</th>
                  {(isAdmissionDiscountChangable ||
                    isFinancialDiscountChangable) &&
                    canModifyDiscounts && <th className="w-1/7">Action</th>}
                </tr>
              </thead>
              <tbody>
                {appliedDiscounts
                  .filter((discount) => {
                    if (
                      (showAdmissionDiscounts &&
                        discount.type === "Admission") ||
                      (showFinancialDiscounts && discount.type === "Financial")
                    ) {
                      return discount;
                    }
                  })
                  .map((discount, index) => {
                    return (
                      <tr key={index} className="border-b even:bg-slate-50">
                        <Scope path={`discounts[${index}]`}>
                          <Input
                            type="hidden"
                            name="filial_discount_list_id"
                            defaultValue={discount.id}
                            InputContext={InputContext}
                          />
                          <Input
                            type="hidden"
                            name="start_date"
                            defaultValue={discount.start_date}
                            InputContext={InputContext}
                          />
                          {!showFinancialDiscounts && (
                            <Input
                              type="hidden"
                              name="end_date"
                              defaultValue={discount.end_date}
                              InputContext={InputContext}
                            />
                          )}
                          <td>{discount.name}</td>
                          <td>
                            {discount.value} {discount.percent ? "%" : "$"}
                          </td>
                          {showFinancialDiscounts && (
                            <>
                              <td>
                                {discount.start_date
                                  ? format(
                                      parseISO(discount.start_date),
                                      "MM/dd/yyyy"
                                    )
                                  : ""}
                              </td>
                              <td className="max-w-16">
                                <DatePicker
                                  name={`end_date`}
                                  // title="End Date"
                                  InputContext={InputContext}
                                  defaultValue={
                                    discount.end_date
                                      ? parseISO(discount.end_date)
                                      : null
                                  }
                                  className="w-full bg-transparent text-center border-b border-red-500 border-dashed"
                                />
                              </td>
                            </>
                          )}
                          <td>
                            <Input
                              type="hidden"
                              name="type"
                              defaultValue={discount.type}
                              InputContext={InputContext}
                            />
                            {discount.type}
                          </td>
                          <td>
                            <Input
                              type="hidden"
                              name="applied_at"
                              defaultValue={discount.applied_at}
                              InputContext={InputContext}
                            />
                            {discount.applied_at}
                          </td>
                          <td>
                            {((isAdmissionDiscountChangable &&
                              discount.type === "Admission") ||
                              (isFinancialDiscountChangable &&
                                discount.type === "Financial")) &&
                              canModifyDiscounts && (
                                <button
                                  type="button"
                                  disabled={discount.has_paid_invoice === true}
                                  onClick={() => {
                                    handleDiscount({
                                      id: discount.id,
                                      el: {
                                        value: false,
                                      },
                                      priceLists,
                                      discountLists,
                                    });
                                  }}
                                  title={discount.has_paid_invoice ? "Cannot remove: invoice already paid" : undefined}
                                  className={`w-full text-xs px-2 py-1 rounded-md flex flex-row items-center justify-center gap-2 ${discount.has_paid_invoice ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                                >
                                  <Trash2 size={12} />
                                  Remove
                                </button>
                              )}
                          </td>
                        </Scope>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </InputLine>
          {canModifyDiscounts && (
            <InputLine title="Available Discounts">
              {isAdmissionDiscountChangable && (
                <>
                  <SelectPopover
                    name="admissionDiscountChoose"
                    title="Admission Discounts"
                    isSearchable
                    grow
                    options={discountOptions.filter(
                      (discount) => discount.type === "Admission"
                    )}
                    InputContext={InputContext}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleDiscount({
                        id: generalForm.current.getFieldValue(
                          "prices.admissionDiscountChoose"
                        ),
                        el: { value: true },
                        priceLists,
                        discountLists,
                      });
                    }}
                    className="text-xs bg-gray-100 px-4 py-2 mt-4 rounded-md border cursor-pointer flex flex-row items-center justify-center gap-2 hover:bg-gray-200"
                  >
                    Apply
                  </button>
                </>
              )}
              {isFinancialDiscountChangable && (
                <>
                  <SelectPopover
                    name="financialDiscountChoose"
                    title="Financial Discounts"
                    isSearchable
                    grow
                    options={discountOptions.filter(
                      (discount) => discount.type === "Financial"
                    )}
                    InputContext={InputContext}
                  />
                  <DatePicker
                    name="financialDiscountFromDate"
                    title="Start Date"
                    required
                    defaultValue={new Date()}
                    InputContext={InputContext}
                  />
                  <DatePicker
                    name="financialDiscountUntilDate"
                    title="End Date"
                    InputContext={InputContext}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      handleDiscount({
                        id: generalForm.current.getFieldValue(
                          "prices.financialDiscountChoose"
                        ),
                        start_date: generalForm.current.getFieldValue(
                          "prices.financialDiscountFromDate"
                        ),
                        end_date: generalForm.current.getFieldValue(
                          "prices.financialDiscountUntilDate"
                        ),
                        el: { value: true },
                        priceLists,
                        discountLists,
                      });
                    }}
                    className="text-xs bg-gray-100 px-4 py-2 mt-4 rounded-md border cursor-pointer flex flex-row items-center justify-center gap-2 hover:bg-gray-200"
                  >
                    Apply
                  </button>
                </>
              )}
            </InputLine>
          )}
        </>
      )}
    </Scope>
  );
}

export default PricesSimulation;
